import { type QueryClient, QueryClientProvider } from "@tanstack/react-query"
import {
	httpBatchLink,
	httpSubscriptionLink,
	loggerLink,
	splitLink,
	TRPCClientError,
	type TRPCLink
} from "@trpc/client"
import { createTRPCReact } from "@trpc/react-query"
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server"
import { observable } from "@trpc/server/observable"
import posthog from "posthog-js"
import { type ReactNode, useState } from "react"
import SuperJSON from "superjson"
import { getApiBaseUrl } from "~/lib/client-url"
import { getNativeAuthToken } from "~/lib/native-auth-token"
import type { AppRouter } from "~/trpc"
import { createQueryClient } from "./query-client"

let clientQueryClientSingleton: QueryClient | undefined
const getQueryClient = () => {
	if (typeof window === "undefined") {
		return createQueryClient()
	}
	return (clientQueryClientSingleton ??= createQueryClient())
}

export const api = createTRPCReact<AppRouter>()

export type RouterInputs = inferRouterInputs<AppRouter>
export type RouterOutputs = inferRouterOutputs<AppRouter>

const orbitClientId =
	typeof globalThis.crypto !== "undefined" && "randomUUID" in globalThis.crypto
		? globalThis.crypto.randomUUID()
		: `${Date.now()}-${Math.random()}`

export const getOrbitClientId = () => orbitClientId

const NETWORK_ERROR_PATTERNS = [
	"failed to fetch",
	"load failed",
	"networkerror",
	"network request failed"
]

// Paths already reported this session, so retries do not multiply one blip.
const reportedTransportPaths = new Set<string>()

const errorChain = (error: unknown): Error[] => {
	const chain: Error[] = []
	const pending: unknown[] = [error]
	while (pending.length > 0 && chain.length < 5) {
		const current = pending.shift()
		if (!(current instanceof Error) || chain.includes(current)) continue
		chain.push(current)
		// A SuppressedError holds its errors on .error/.suppressed, not .cause.
		const links = current as {
			cause?: unknown
			error?: unknown
			suppressed?: unknown
		}
		pending.push(links.cause, links.error, links.suppressed)
	}
	return chain
}

// Aborted requests, offline, and teardown are expected transport blips, not bugs.
const isExpectedTransportError = (error: unknown): boolean => {
	if (typeof navigator !== "undefined" && navigator.onLine === false) {
		return true
	}
	return errorChain(error).some((entry) => {
		if (entry.name === "AbortError") return true
		const message = entry.message.toLowerCase()
		return NETWORK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
	})
}

const isApiUnavailableError = (error: TRPCClientError<AppRouter>) => {
	if (error.data?.code) return false
	const chain = errorChain(error)
	if (chain.some((entry) => entry.name === "AbortError")) return false
	return chain.some((entry) => {
		const message = entry.message.toLowerCase()
		return (
			NETWORK_ERROR_PATTERNS.some((pattern) => message.includes(pattern)) ||
			message.includes("<!doctype html") ||
			/unexpected token\s+['"]?</i.test(message) ||
			message.includes("json parse error") ||
			message.includes("not valid json") ||
			message.includes("unexpected end of json")
		)
	})
}

const reportTransportException = (
	err: TRPCClientError<AppRouter>,
	path: string,
	type: string
) => {
	if (import.meta.env.DEV || !posthog.__loaded) return
	// Only the transport bucket: failures that carry no tRPC error code.
	if (err.data?.code) return
	if (isExpectedTransportError(err)) return
	if (reportedTransportPaths.has(path)) return
	reportedTransportPaths.add(path)

	posthog.captureException(err, {
		trpc_path: path,
		trpc_type: type,
		// One outage fails many paths at once, so group it as a single issue.
		// Group every other transport variant of one path as a single issue.
		$exception_fingerprint: isApiUnavailableError(err)
			? "trpc_transport:api_unavailable"
			: `trpc_transport:${path}`
	})
}

const captureExceptionLink: TRPCLink<AppRouter> = () => {
	return ({ next, op }) =>
		observable((observer) => {
			const subscription = next(op).subscribe({
				next: (value) => observer.next(value),
				error: (err) => {
					reportTransportException(err, op.path, op.type)
					observer.error(
						isApiUnavailableError(err)
							? new TRPCClientError<AppRouter>("Unable to reach API", {
									cause: err,
									meta: err.meta
								})
							: err
					)
				},
				complete: () => observer.complete()
			})
			return () => subscription.unsubscribe()
		})
}

export function TRPCReactProvider(props: { children: ReactNode }) {
	const queryClient = getQueryClient()

	const [trpcClient] = useState(() => {
		const url = `${getApiBaseUrl()}/api/trpc`
		const liveUpdateClientId = getOrbitClientId()
		const headers = () => {
			const headers = new Headers()
			const token = getNativeAuthToken()
			headers.set("x-trpc-source", "vite-react")
			headers.set("x-orbit-client-id", liveUpdateClientId)
			if (token) headers.set("authorization", `Bearer ${token}`)
			if (typeof window !== "undefined" && posthog.__loaded) {
				headers.set("x-posthog-distinct-id", posthog.get_distinct_id())
				headers.set("x-posthog-session-id", posthog.get_session_id())
			}
			return headers
		}

		return api.createClient({
			links: [
				loggerLink({
					enabled: () => import.meta.env.DEV
				}),
				captureExceptionLink,
				splitLink({
					condition: (op) => op.type === "subscription",
					true: httpSubscriptionLink({
						transformer: SuperJSON,
						url,
						connectionParams: () => {
							const token = getNativeAuthToken()
							return {
								clientId: liveUpdateClientId,
								source: "vite-react",
								...(token ? { authorization: `Bearer ${token}` } : {})
							}
						},
						eventSourceOptions: () => ({ withCredentials: true })
					}),
					false: httpBatchLink({
						transformer: SuperJSON,
						url,
						fetch: (url, options) =>
							fetch(url, { ...options, credentials: "include" }),
						headers
					})
				})
			]
		})
	})

	return (
		<QueryClientProvider client={queryClient}>
			<api.Provider client={trpcClient} queryClient={queryClient}>
				{props.children}
			</api.Provider>
		</QueryClientProvider>
	)
}
